import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { JobBidsStatusBuyer, JSONApiDataObject, MilestonesData, ReduxEntity } from 'Douleutaras/types';
import { RootState } from 'Douleutaras/redux/store';
import { makeRequest } from 'Douleutaras/services/apiClient';
import normalize from 'json-api-normalizer';
import { NEED_HELP_MILESTONES } from 'Douleutaras/constants/ccf/need-help-options';
import { jobBidsSlice } from 'Douleutaras/redux/data/jobBids/jobBidsSlice';
import jobPriceDefiningAnswersSlice from 'Douleutaras/redux/data/jobPriceDefiningAnswers/jobPriceDefiningAnswersSlice';

export const bidBuyerStatusEndpoint = (jobId: string, bidId: string) => `api/public/v3/job/${jobId}/bids/${bidId}/bid-status-buyer-update/`;
interface BidBuyerArgs {
  id?: string; // model id
  jobId: string;
  jobStartDate?: string;
  bidId: string;
  milestone?: NEED_HELP_MILESTONES;
  newEntry?: boolean;
  price?: string;
  priceDefiningAnswers?: Array<{
    questionId: number;
    answerValueId: number;
  }>;
}

const jobBidsStatusBuyerAdapter = createEntityAdapter<JSONApiDataObject<JobBidsStatusBuyer>>({});

const parseResponse = (responseData, endpoint: string, bidId: string, api, insertOrUpdate: boolean) => {
  const id = responseData?.data?.id;
  const { jobBidsAddBidsBuyerStatusRelation, jobBidAddJobDefiningAnswersRelation } = jobBidsSlice.actions;
  const { jobPriceDefiningAnswersUpsert } = jobPriceDefiningAnswersSlice.actions;
  const responseNormalized = normalize(responseData);

  if (responseNormalized.answers && insertOrUpdate) {
    api.dispatch(
      jobPriceDefiningAnswersUpsert(responseNormalized.answers)
    );
  }

  // Put relationship on the bid
  if (id && insertOrUpdate) {
    api.dispatch(
      jobBidsAddBidsBuyerStatusRelation({
        bidId,
        id
      })
    );

    // Store jobDefiningAnswers Relation to bid
    api.dispatch(
      jobBidAddJobDefiningAnswersRelation({
        bidId,
        data: responseData.data.relationships.givenPriceDefiningAnswers.data
      })
    );
  }

  return normalize(responseData, { endpoint });
};

/**
 * Gets bid-status-buyer-update model, relation of bid
 */
export const getBidBuyerStatus = createAsyncThunk(
  'jobBids/getBidStatusBuyerUpdate',
  async ({ jobId, bidId }: BidBuyerArgs, api) => {
    const endpoint = bidBuyerStatusEndpoint(jobId, bidId);
    const response = await makeRequest(endpoint, {
      method: 'GET'
    })(api);

    return parseResponse(response.data, endpoint, bidId, api, false);
  }
);

/**
 * Creates bid-status-buyer-update model entry, relation of bid
 */
export const createBidBuyerStatus = createAsyncThunk(
  'jobBids/postBidStatusBuyerUpdate',
  async ({ jobId, bidId, milestone, price, jobStartDate, priceDefiningAnswers }: BidBuyerArgs, api) => {
    const endpoint = bidBuyerStatusEndpoint(jobId, bidId);
    const response = await makeRequest(bidBuyerStatusEndpoint(jobId, bidId), {
      method: 'POST',
      includes: [ 'given_price_defining_answers' ],
      body: {
        data: {
          type: 'bid-status-buyer-update',
          attributes: {
            milestone,
            price,
            jobStartDate,
            priceDefiningAnswers
          }
        }
      }
    })(api);

    return parseResponse(response.data, endpoint, bidId, api, true);
  }
);

/**
 * Updates bid-status-buyer-update model, relation of bid
 */
export const updateBidBuyerStatus = createAsyncThunk(
  'jobBids/updateBidStatusBuyerUpdate',
  async ({ id, jobId, bidId, milestone, price, jobStartDate, priceDefiningAnswers  }: BidBuyerArgs, api) => {
    const endpoint = bidBuyerStatusEndpoint(jobId, bidId);
    const response = await makeRequest(`${bidBuyerStatusEndpoint(jobId, bidId)}${id}/`, {
      method: 'PATCH',
      includes: [ 'given_price_defining_answers' ],
      body: {
        data: {
          id,
          type: 'bid-status-buyer-update',
          attributes: {
            milestone,
            price,
            jobStartDate,
            priceDefiningAnswers
          }
        }
      }
    })(api);

    return parseResponse(response.data, endpoint, bidId, api, true);
  }
);

export const JOB_BIDS_STATUS_BUYER_MODEL_NAME = 'jobBidsStatusBuyer';

export const jobBidsStatusBuyerSlice = createSlice({

  name: JOB_BIDS_STATUS_BUYER_MODEL_NAME,
  initialState: jobBidsStatusBuyerAdapter.getInitialState({
    loading: false,
    error: false,
    meta: {},
    entities: {}
  } as ReduxEntity<JobBidsStatusBuyer, MilestonesData>),
  reducers: {
    bidBuyerStatusAdded: (state, action) => {
      jobBidsStatusBuyerAdapter.setMany(state, action.payload);
    }
  },
  extraReducers: builder => {
    builder.addCase(getBidBuyerStatus.fulfilled, (state, action) => {
      if (action.payload.bidStatusBuyerUpdate) {
        jobBidsStatusBuyerAdapter.setMany(state, action.payload.bidStatusBuyerUpdate);
      }
      state.meta = { ...state.meta, ...action.payload.meta };
    });
    builder.addCase(updateBidBuyerStatus.fulfilled, (state, action) => {
      jobBidsStatusBuyerAdapter.setMany(state, action.payload.bidStatusBuyerUpdate);

      state.meta = { ...state.meta, ...action.payload.meta };
    });
    builder.addCase(createBidBuyerStatus.fulfilled, (state, action) => {
      jobBidsStatusBuyerAdapter.setMany(state, action.payload.bidStatusBuyerUpdate);

      state.meta = { ...state.meta, ...action.payload.meta };
    });
  }
});

export const bidBuyerStatusSelectors = jobBidsStatusBuyerAdapter.getSelectors((state: RootState) => state.jobBidsStatusBuyer);
